import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Interfaz para los resultados de autocompletado de direcciones
 */
interface AddressPrediction {
  description: string;
  place_id: string;
}

/**
 * Interfaz para los resultados de geocodificación
 */
interface AddressInfo {
  locality: string;
  province: string;
  country: string;
  latitude: number;
  longitude: number;
}

/**
 * Interfaz para los componentes de dirección
 */
interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AddressAutocompleteService {
  private autocompleteService: any;
  private geocoder: any;

  constructor() {
    // Inicializar los servicios de Google Maps cuando estén disponibles
    try {
      if (window['google'] && window['google'].maps) {
        this.initializeServices();
      } else {
        // Si google maps no está cargado todavía, esperar a que se cargue
        console.warn('Google Maps API no está disponible. Algunas funciones pueden no estar operativas.');
      }
    } catch (error) {
      console.error('Error inicializando servicios de Google Maps:', error);
    }
  }

  /**
   * Inicializa los servicios de Google Maps
   */
  private initializeServices(): void {
    try {
      this.autocompleteService = new google.maps.places.AutocompleteService();
      this.geocoder = new google.maps.Geocoder();
    } catch (error) {
      console.error('Error al inicializar servicios de Google Maps:', error);
    }
  }

  /**
   * Busca sugerencias de dirección basadas en un texto de entrada
   * @param input Texto para buscar direcciones
   * @returns Observable con las sugerencias de autocompletado
   */
  getSuggestions(input: string): Observable<AddressPrediction[]> {
    return new Observable(observer => {
      if (!input || input.trim() === '') {
        observer.next([]);
        observer.complete();
        return;
      }

      // Asegurar que los servicios estén inicializados
      if (!this.autocompleteService) {
        this.initializeServices();
        if (!this.autocompleteService) {
          console.warn('No se puede inicializar AutocompleteService');
          observer.next([]);
          observer.complete();
          return;
        }
      }

      // Parámetros más compatibles para la API
      const request: any = {
        input: input
      };
      
      // Opcionalmente añadimos restricciones si están disponibles
      try {
        request.types = ['address'];
        request.componentRestrictions = { country: 'ar' }; // Restringir a Argentina, cambiar según necesidad
      } catch (error) {
        console.warn('No se pueden aplicar todas las restricciones:', error);
      }

      this.autocompleteService.getPlacePredictions(
        request,
        (predictions: AddressPrediction[], status: any) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            observer.next(predictions);
          } else {
            observer.next([]);
          }
          observer.complete();
        }
      );
    });
  }

  /**
   * Obtiene las coordenadas geográficas y detalles de una dirección
   * @param address Dirección a geocodificar
   * @returns Promise con la información de la dirección
   */
  getGeocode(address: string): Promise<AddressInfo> {
    return new Promise((resolve, reject) => {
      if (!this.geocoder) {
        this.initializeServices();
        if (!this.geocoder) {
          reject('Geocoder no está disponible');
          return;
        }
      }

      this.geocoder.geocode({ address }, (results: any[], status: any) => {
        if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
          try {
            const addressComponents = results[0].address_components || [];
            const location = results[0].geometry.location;
            
            const addressInfo: AddressInfo = {
              locality: this.getAddressComponent(addressComponents, 'locality'),
              province: this.getAddressComponent(addressComponents, 'administrative_area_level_1'),
              country: this.getAddressComponent(addressComponents, 'country'),
              latitude: location.lat(),
              longitude: location.lng()
            };
            
            resolve(addressInfo);
          } catch (error) {
            console.error('Error procesando resultados de geocodificación:', error);
            reject(error);
          }
        } else {
          reject('No se pudo obtener la geolocalización');
        }
      });
    });
  }

  /**
   * Obtiene un componente específico de la dirección
   */
  private getAddressComponent(components: AddressComponent[], type: string): string {
    const component = components.find(c => c.types.includes(type));
    return component ? component.long_name : '';
  }
}